import { KPI_TEXT_SHADOW } from '@/components/ui/KpiCard';
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, Clock, Download, IndianRupee, PackageX, type LucideIcon } from '@/components/icons';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { FilterBar, FilterField } from '@/components/ui/FilterBar';
import { PeriodPicker, REPORT_UNITS, usePeriod } from '@/components/ui/PeriodPicker';
import { Table } from '@/components/ui/Table';
import { Badge } from '@/components/ui/Badge';
import { Select } from '@/components/form/Select';
import {
  fulfillmentExportPath,
  getFulfillmentKpis,
  listFulfillment,
  type FulfillmentRow,
} from '@/api/fulfillment';
import { downloadExcel } from '@/lib/downloadExcel';
import { formatDate, formatINR } from '@/lib/format';
import { orderRef } from '@/lib/orderRef';

const STAGE_TONE: Record<string, 'accent' | 'success' | 'muted' | 'danger'> = {
  new: 'muted',
  reviewed: 'muted',
  'do-created': 'accent',
  'credit-pending': 'accent',
  'credit-approved': 'accent',
  'pick-pending': 'accent',
  'pick-done': 'accent',
  'packing-done': 'accent',
  'grn-out': 'accent',
  invoiced: 'accent',
  'gate-out': 'accent',
  fulfilled: 'success',
};

export function FulfillmentPage() {
  const [stage, setStage] = useState('');

  // Order date — All / Date (from–to) / Month / Quarter / Year.
  const period = usePeriod();
  const { range } = period;
  const filter = useMemo(
    () => ({
      stage: stage || undefined,
      from: range?.from,
      to: range?.to,
    }),
    [stage, range],
  );

  const rowsQuery = useQuery({
    queryKey: ['fulfillment', filter],
    queryFn: () => listFulfillment(filter),
    refetchInterval: 60_000,
  });
  const kpisQuery = useQuery({
    queryKey: ['fulfillment-kpis'],
    queryFn: getFulfillmentKpis,
    refetchInterval: 60_000,
  });
  const rows = rowsQuery.data ?? [];
  const kpis = kpisQuery.data;

  return (
    <div>
      <SectionHeader
        title="Fulfillment"      />

      <div className="mb-2 grid grid-cols-2 gap-1.5 sm:mb-2 sm:gap-3 md:grid-cols-4">
        <KpiCard label="Delivered" value={kpis?.delivered ?? '—'} tone="success" icon={CheckCircle2} />
        <KpiCard label="In Transit" value={kpis?.inTransit ?? '—'} tone="accent" icon={Clock} />
        <KpiCard
          label="Partial / Pending"
          value={kpis?.partialPending ?? '—'}
          tone="warning"
          icon={PackageX}
        />
        <KpiCard
          label="Invoiced Value"
          value={kpis ? formatINR(kpis.totalInvoicedValue) : '—'}
          tone="primary"
          icon={IndianRupee}
        />
      </div>

      <FilterBar>
        <FilterField label="Stage">
          <Select value={stage} onChange={(e) => setStage(e.target.value)}>
            <option value="">All stages</option>
            <option value="new">New</option>
            <option value="reviewed">Reviewed</option>
            <option value="do-created">DO Created</option>
            <option value="credit-pending">Credit Pending</option>
            <option value="pick-pending">Pick Pending</option>
            <option value="pick-done">Pick Done</option>
            <option value="packing-done">Packing Done</option>
            <option value="grn-out">Outward GRN</option>
            <option value="invoiced">Invoiced</option>
            <option value="gate-out">Gate-Out</option>
            <option value="fulfilled">Fulfilled</option>
          </Select>
        </FilterField>
        <FilterField label="Period" className="col-span-3 sm:col-auto">
          <PeriodPicker {...period.picker} units={REPORT_UNITS} className="justify-start" />
        </FilterField>
        {/* Merged chip: visible on both viewports, right-aligned inside the filter block. */}
        <button
          type="button"
          onClick={() => downloadExcel(fulfillmentExportPath(filter), 'GRHAMOY_Fulfillment.xlsx')}
          disabled={rows.length === 0}
          title={rows.length === 0 ? 'No rows to export' : 'Export current filtered view to Excel'}
          className="col-start-3 inline-flex h-7 items-center justify-center gap-1 rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)] px-2 text-[11px] font-bold text-[var(--text-secondary)] shadow-sm transition hover:border-success hover:text-success disabled:opacity-40 sm:col-start-auto sm:ml-auto sm:h-8 sm:self-end sm:px-3 sm:text-xs"
        >
          <Download size={12} /> Export
        </button>
      </FilterBar>

      <Table<FulfillmentRow>
        storageKey="fulfillment"
        title="Enquiry Pipeline"
        count={`${rows.length} records`}
        rows={rows}
        rowKey={(r) => r.enqId}
        loading={rowsQuery.isFetching && rows.length === 0}
        columns={[
          {
            key: 'enq',
            header: 'ENQ #',
            cell: (r) => <span className="font-extrabold text-accent" title={r.enqNo}>{orderRef(r)}</span>,
          },
          { key: 'date', header: 'Date', cell: (r) => formatDate(r.enqDate) },
          { key: 'party', header: 'Customer', cell: (r) => <span className="font-bold">{r.partyName}</span> },
          {
            key: 'value',
            header: 'Est Value',
            align: 'right',
            cell: (r) => formatINR(r.estOrderValue),
          },
          {
            key: 'stage',
            header: 'Stage',
            cell: (r) => <Badge tone={STAGE_TONE[r.stage] ?? 'muted'}>{r.stage}</Badge>,
          },
          { key: 'do', header: 'DO', cell: (r) => r.doRef ?? '—' },
          { key: 'pick', header: 'Pick', cell: (r) => r.pickStage ?? '—' },
          { key: 'pack', header: 'Pack', cell: (r) => r.packStage ?? '—' },
          {
            key: 'grnOut',
            header: 'Outward GRN',
            cell: (r) => r.outwardGrnRef ?? '—',
          },
          {
            key: 'invoice',
            header: 'Invoice',
            cell: (r) =>
              r.invoiceRef ? (
                <span className="inline-flex items-center gap-1.5">
                  {r.invoiceRef}
                  <Badge tone="muted">{r.invoiceStatus}</Badge>
                </span>
              ) : (
                '—'
              ),
          },
          { key: 'gp', header: 'Gate Pass', cell: (r) => r.gatePassRef ?? '—' },
          {
            key: 'delivery',
            header: 'Delivery',
            cell: (r) =>
              r.deliveryStatus ? (
                <Badge
                  tone={
                    r.deliveryStatus === 'Delivered'
                      ? 'success'
                      : r.deliveryStatus === 'In Transit'
                        ? 'accent'
                        : 'muted'
                  }
                >
                  {r.deliveryStatus}
                </Badge>
              ) : (
                '—'
              ),
          },
          {
            key: 'delivered',
            header: 'Delivered',
            cell: (r) => (r.deliveryDate ? formatDate(r.deliveryDate) : '—'),
          },
        ]}
      />
    </div>
  );
}

const KPI_ACCENT: Record<'success' | 'accent' | 'warning' | 'primary', { color: string; bg: string }> = {
  success: { color: '#10b981', bg: 'rgba(16,185,129,0.10)' },
  accent:  { color: '#FF8C00', bg: 'rgba(255,140,0,0.10)' },
  warning: { color: '#ef4444', bg: 'rgba(239,68,68,0.10)' },
  primary: { color: '#3b82f6', bg: 'rgba(59,130,246,0.10)' },
};

function KpiCard({
  label,
  value,
  tone,
  icon: Icon,
}: {
  label: string;
  value: React.ReactNode;
  tone: 'success' | 'accent' | 'warning' | 'primary';
  icon: LucideIcon;
}) {
  const { color } = KPI_ACCENT[tone];
  return (
    <div
      className="relative overflow-hidden rounded-lg p-2 text-white shadow-sm transition-transform hover:-translate-y-0.5 sm:rounded-xl sm:p-4"
      style={{ background: color, textShadow: KPI_TEXT_SHADOW }}
    >
      <Icon className="absolute -bottom-3 -right-3 h-16 w-16 sm:h-20 sm:w-20" style={{ color: '#fff', opacity: 0.18 }} strokeWidth={1.25} />
      <div className="relative">
        <div className="text-[9px] font-extrabold uppercase tracking-widest text-white/90 sm:text-[10px]">{label}</div>
        <div className="mt-0.5 text-lg font-extrabold sm:mt-2 sm:text-2xl">{value}</div>
      </div>
    </div>
  );
}
